import { File } from "lucide-react";
import toast from "react-hot-toast";
import {
  FaArchive,
  FaAtom,
  FaBook,
  FaBookOpen,
  FaBoxOpen,
  FaCalculator,
  FaCalendarAlt,
  FaCamera,
  FaChartBar,
  FaCode,
  FaCog,
  FaCube,
  FaDatabase,
  FaDesktop,
  FaDownload,
  FaEnvelope,
  FaFile,
  FaFileAlt,
  FaFileArchive,
  FaFileCode,
  FaFileCsv,
  FaFileExcel,
  FaFilePdf,
  FaFilePowerpoint,
  FaFileWord,
  FaFont,
  FaGamepad,
  FaGlobe,
  FaGraduationCap,
  FaHardHat,
  FaImage,
  FaKey,
  FaMapMarkedAlt,
  FaMobileAlt,
  FaMusic,
  FaMusic as FaMusicNote,
  FaPaintBrush,
  FaPalette,
  FaProjectDiagram,
  FaRobot,
  FaServer,
  FaShieldAlt,
  FaTerminal,
  FaVectorSquare,
  FaVideo,
} from "react-icons/fa";
import { AgentStatus, AUTONOMOUS_AGENT_STATUS } from "constants/agents";

// Export i18n helpers
export {
  getStatusTranslationKey,
  getTypeTranslationKey,
  getAccessTranslationKey,
  getAccessOptionsWithI18n,
  getSuggestionTypeTranslationKey,
  getSubscriptionFrequencyTranslationKey,
  getAgentStatusTranslationKey,
} from "./i18n-helpers";

export const getFileIcon = (fileName: string, props?: any) => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (!extension) return <File className="h-5 w-5" {...props} />;

  switch (extension) {
    // Images
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "bmp":
    case "tiff":
    case "tif":
    case "webp":
    case "ico":
      return <FaImage className="h-5 w-5 text-green-500" {...props} />;

    case "svg":
      return <FaVectorSquare className="h-5 w-5 text-green-600" {...props} />;

    case "psd":
    case "ai":
    case "sketch":
    case "figma":
      return <FaPalette className="h-5 w-5 text-purple-600" {...props} />;

    case "raw":
    case "cr2":
    case "nef":
    case "arw":
      return <FaCamera className="h-5 w-5 text-orange-500" {...props} />;

    // Audio
    case "mp3":
    case "wav":
    case "flac":
    case "aac":
    case "ogg":
    case "wma":
    case "m4a":
      return <FaMusic className="h-5 w-5 text-purple-500" {...props} />;

    case "midi":
    case "mid":
      return <FaMusicNote className="h-5 w-5 text-indigo-500" {...props} />;

    // Video
    case "mp4":
    case "avi":
    case "mkv":
    case "mov":
    case "wmv":
    case "flv":
    case "webm":
    case "m4v":
    case "3gp":
      return <FaVideo className="h-5 w-5 text-red-500" {...props} />;

    // Archives
    case "zip":
    case "rar":
    case "7z":
    case "tar":
    case "gz":
    case "bz2":
    case "xz":
    case "dmg":
    case "iso":
      return <FaArchive className="h-5 w-5 text-yellow-500" {...props} />;

    // Documents - Microsoft Office
    case "doc":
    case "docx":
      return <FaFileWord className="h-5 w-5 text-blue-600" {...props} />;

    case "xls":
    case "xlsx":
      return <FaFileExcel className="h-5 w-5 text-green-600" {...props} />;

    case "ppt":
    case "pptx":
      return (
        <FaFilePowerpoint className="h-5 w-5 text-orange-600" {...props} />
      );

    // Documents - Other
    case "pdf":
      return <FaFilePdf className="h-5 w-5 text-red-600" {...props} />;

    case "txt":
    case "rtf":
      return <FaFileAlt className="h-5 w-5 text-gray-600" {...props} />;

    case "md":
    case "markdown":
      return <FaBookOpen className="h-5 w-5 text-blue-500" {...props} />;

    case "tex":
    case "latex":
      return <FaGraduationCap className="h-5 w-5 text-green-700" {...props} />;

    // Data Files
    case "csv":
      return <FaFileCsv className="h-5 w-5 text-green-500" {...props} />;

    case "json":
    case "xml":
    case "yaml":
    case "yml":
      return <FaFileCode className="h-5 w-5 text-orange-500" {...props} />;

    case "sql":
    case "db":
    case "sqlite":
    case "mdb":
      return <FaDatabase className="h-5 w-5 text-blue-700" {...props} />;

    // Programming Languages
    case "js":
    case "jsx":
    case "ts":
    case "tsx":
      return <FaCode className="h-5 w-5 text-yellow-600" {...props} />;

    case "html":
    case "htm":
      return <FaGlobe className="h-5 w-5 text-orange-600" {...props} />;

    case "css":
    case "scss":
    case "sass":
    case "less":
      return <FaPaintBrush className="h-5 w-5 text-blue-500" {...props} />;

    case "py":
      return <FaCode className="h-5 w-5 text-green-600" {...props} />;

    case "java":
    case "class":
    case "jar":
      return <FaBoxOpen className="h-5 w-5 text-red-600" {...props} />;

    case "c":
    case "cpp":
    case "cc":
    case "h":
    case "hpp":
      return <FaCode className="h-5 w-5 text-blue-700" {...props} />;

    case "cs":
      return <FaCode className="h-5 w-5 text-purple-600" {...props} />;

    case "php":
      return <FaCode className="h-5 w-5 text-indigo-600" {...props} />;

    case "rb":
      return <FaCode className="h-5 w-5 text-red-500" {...props} />;

    case "go":
      return <FaCode className="h-5 w-5 text-cyan-600" {...props} />;

    case "rs":
      return <FaCode className="h-5 w-5 text-orange-700" {...props} />;

    case "swift":
      return <FaCode className="h-5 w-5 text-orange-500" {...props} />;

    case "kt":
    case "kts":
      return <FaCode className="h-5 w-5 text-purple-500" {...props} />;

    case "dart":
      return <FaCode className="h-5 w-5 text-blue-400" {...props} />;

    case "r":
      return <FaChartBar className="h-5 w-5 text-blue-600" {...props} />;

    case "m":
    case "matlab":
      return <FaCalculator className="h-5 w-5 text-orange-600" {...props} />;

    case "scala":
      return <FaCode className="h-5 w-5 text-red-700" {...props} />;

    case "clj":
    case "cljs":
      return <FaCode className="h-5 w-5 text-green-700" {...props} />;

    // Shell Scripts
    case "sh":
    case "bash":
    case "zsh":
    case "fish":
    case "bat":
    case "cmd":
    case "ps1":
      return <FaTerminal className="h-5 w-5 text-gray-700" {...props} />;

    // Configuration Files
    case "ini":
    case "cfg":
    case "conf":
    case "config":
    case "toml":
    case "env":
      return <FaCog className="h-5 w-5 text-gray-600" {...props} />;

    case "dockerfile":
    case "docker":
      return <FaCube className="h-5 w-5 text-blue-600" {...props} />;

    // Build Files
    case "makefile":
    case "make":
    case "cmake":
    case "gradle":
    case "maven":
    case "ant":
      return <FaHardHat className="text-brown-600 h-5 w-5" {...props} />;

    case "package":
    case "lock":
    case "yarn":
    case "npm":
      return <FaBoxOpen className="h-5 w-5 text-red-500" {...props} />;

    // Fonts
    case "ttf":
    case "otf":
    case "woff":
    case "woff2":
    case "eot":
      return <FaFont className="h-5 w-5 text-purple-700" {...props} />;

    // 3D Files
    case "obj":
    case "fbx":
    case "dae":
    case "3ds":
    case "blend":
    case "max":
    case "maya":
    case "c4d":
      return <FaCube className="h-5 w-5 text-indigo-600" {...props} />;

    // CAD Files
    case "dwg":
    case "dxf":
    case "step":
    case "stp":
    case "iges":
    case "igs":
      return <FaProjectDiagram className="h-5 w-5 text-blue-800" {...props} />;

    // eBooks
    case "epub":
    case "mobi":
    case "azw":
    case "azw3":
      return <FaBook className="text-brown-600 h-5 w-5" {...props} />;

    // Executable Files
    case "exe":
    case "msi":
    case "deb":
    case "rpm":
    case "pkg":
    case "app":
      return <FaDesktop className="h-5 w-5 text-gray-800" {...props} />;

    case "apk":
    case "ipa":
      return <FaMobileAlt className="h-5 w-5 text-green-700" {...props} />;

    // Games
    case "unity":
    case "unreal":
    case "godot":
      return <FaGamepad className="h-5 w-5 text-purple-800" {...props} />;

    // Security
    case "pem":
    case "crt":
    case "cer":
    case "key":
    case "p12":
    case "pfx":
    case "jks":
      return <FaKey className="h-5 w-5 text-yellow-700" {...props} />;

    case "gpg":
    case "pgp":
    case "asc":
      return <FaShieldAlt className="h-5 w-5 text-green-800" {...props} />;

    // Virtual Machine
    case "vmdk":
    case "vdi":
    case "vhd":
    case "vhdx":
    case "ova":
    case "ovf":
      return <FaServer className="h-5 w-5 text-gray-700" {...props} />;

    // Specialized File Types
    case "log":
      return <FaFileAlt className="h-5 w-5 text-yellow-600" {...props} />;

    case "torrent":
      return <FaDownload className="h-5 w-5 text-green-600" {...props} />;

    case "ics":
    case "ical":
      return <FaCalendarAlt className="h-5 w-5 text-blue-500" {...props} />;

    case "vcf":
    case "vcard":
      return <FaEnvelope className="h-5 w-5 text-blue-600" {...props} />;

    case "kml":
    case "kmz":
    case "gpx":
      return <FaMapMarkedAlt className="h-5 w-5 text-red-600" {...props} />;

    // Backup Files
    case "bak":
    case "backup":
    case "old":
    case "orig":
      return <FaFileArchive className="h-5 w-5 text-gray-500" {...props} />;

    // Machine Learning / AI
    case "pkl":
    case "pickle":
    case "h5":
    case "hdf5":
    case "pt":
    case "pth":
    case "onnx":
    case "pb":
    case "tflite":
    case "mlmodel":
      return <FaRobot className="h-5 w-5 text-purple-700" {...props} />;

    // Scientific Data
    case "mat":
    case "fits":
    case "hdf":
    case "nc":
    case "cdf":
      return <FaAtom className="h-5 w-5 text-cyan-700" {...props} />;

    // Default fallback
    default:
      return <FaFile className="h-5 w-5 text-gray-500" {...props} />;
  }
};

export function formatFileSize(bytes: number): string {
  if (isNaN(bytes) || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size = size / 1024;
    i++;
  }
  // Show up to 2 decimals for KB and above, no decimals for bytes
  return i === 0 ? `${size} ${units[i]}` : `${size.toFixed(2)} ${units[i]}`;
}

// Add dark: support for agent status colors
export const getAgentStatusColor = (status: string) => {
  switch (status) {
    case AgentStatus.READY:
      return "bg-green-500 dark:bg-green-600";
    case AgentStatus.TRAINING:
      return "bg-yellow-500 dark:bg-yellow-600";
    case AgentStatus.ERROR:
      return "bg-red-500 dark:bg-red-600";
    case AUTONOMOUS_AGENT_STATUS.SCHEDULING:
      return "bg-blue-500 dark:bg-blue-600";
    case AUTONOMOUS_AGENT_STATUS.IDLE:
      return "bg-gray-500 dark:bg-gray-600";
    default:
      return "bg-gray-500 dark:bg-gray-600";
  }
};

// Document status colors for badge display
export const getDocumentStatusColor = (status: string) => {
  switch (status) {
    case "Processing":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case "Succeed":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "Failed":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
};

export const handleAxiosAndCaughtError = (err: any) => {
  if (err && typeof err === "object" && "response" in err) {
    const apiError = err as {
      response?: {
        data?: { detail?: string };
        config?: { url?: string };
      };
    };
    const detail = apiError?.response?.data?.detail;
    const url = apiError?.response?.config?.url;
    if (url) {
      console.error("Request URL:", url);
    }
    toast.error(typeof detail === "string" ? detail : "Something went wrong");
    throw new Error(apiError.response?.data?.detail) || err;
  }
  toast.error("Somthing went wrong", {
    duration: 2000,
  });
  throw err;
};

/**
 * Validates JSON string and returns error message if invalid
 * @param jsonString - The JSON string to validate
 * @param requireObject - Whether to require the JSON to be an object (default: true)
 * @returns Empty string if valid, error message if invalid
 */
export const validateJson = (
  jsonString: string,
  requireObject: boolean = true
): string => {
  if (!jsonString.trim()) return "";

  try {
    const parsed = JSON.parse(jsonString);

    // Additional validation: ensure it's an object (not array, string, number, etc.)
    if (
      requireObject &&
      (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
    ) {
      return "JSON must be an object (use curly braces {})";
    }

    return "";
  } catch (e) {
    const error = e as Error;
    if (error.message.includes("Unexpected token")) {
      return "Invalid JSON syntax - check quotes, commas, and brackets";
    }
    if (error.message.includes("Unexpected end")) {
      return "Incomplete JSON - missing closing bracket or quote";
    }
    return "Invalid JSON format";
  }
};
